import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileText, ShieldCheck } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadError } from '../components/layout/LoadError';
import { ProtectedPolicyPdf } from '../components/domain/ProtectedPolicyPdf';
import { SignatureSvgPad } from '../components/domain/SignatureSvgPad';
import { useToast } from '../components/ui/toast';
import { useAcknowledgePolicy, useMyPolicy } from '../hooks';
import { ApiError } from '../lib/api-client';


function PolicyReadSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดเอกสาร">
      <div className="card p-4 space-y-2">
        <div className="h-5 w-2/3 bg-gray-100 rounded" />
        <div className="h-3 w-1/3 bg-gray-100 rounded" />
      </div>
      <div className="card h-[65vh] bg-gray-100" />
    </div>
  );
}

export function PolicyRead() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useMyPolicy(id);
  const acknowledge = useAcknowledgePolicy();
  const toast = useToast();
  const startedAt = useRef<number>(Date.now());
  const activePolicyKey = useRef('');
  const [reachedEnd, setReachedEnd] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const key = `${data.policy.id}:${data.policy.version}`;
    if (activePolicyKey.current === key) return;
    activePolicyKey.current = key;
    startedAt.current = Date.now();
    setReachedEnd(Boolean(data.policy.acked || !data.policy.ack_required));
    setConfirmed(false);
    setSignature(null);
  }, [data]);

  async function submitAck() {
    if (!id || !data) return;
    const seconds = Math.max(0, Math.floor((Date.now() - startedAt.current) / 1000));
    try {
      const result = await acknowledge.mutateAsync({
        id,
        read_seconds: seconds,
        signature_svg: data.policy.ack_mode === 'signature' ? signature ?? undefined : undefined,
      });
      toast.success(result.already_acked ? 'เอกสารฉบับนี้รับทราบไว้แล้ว' : 'บันทึกการรับทราบเรียบร้อยแล้ว');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'บันทึกการรับทราบไม่สำเร็จ กรุณาลองใหม่');
    }
  }

  if (isLoading) return <PageLayout title="อ่านเอกสาร"><PolicyReadSkeleton /></PageLayout>;
  if (error || !data) {
    const status = typeof error === 'object' && error !== null && 'status' in error
      ? Number((error as { status: unknown }).status) : 0;
    return (
      <PageLayout title="อ่านเอกสาร">
        <LoadError
          error={error}
          hint={status === 404 ? 'เอกสารนี้อาจไม่อยู่ในขอบเขตของคุณ ยังไม่เผยแพร่ หรือพ้นวันที่มีผลแล้ว' : undefined}
          onRetry={() => void refetch()}
        />
      </PageLayout>
    );
  }

  const policy = data.policy;
  const needsSignature = policy.ack_mode === 'signature';
  const canAck = reachedEnd && confirmed && (!needsSignature || Boolean(signature));

  return (
    <PageLayout title="อ่านเอกสาร">
      <div className="space-y-4 pb-8">
        <section className="card p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-brand-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h1 className="font-semibold break-words">{policy.title}</h1>
              <p className="text-xs text-gray-500 mt-1">เลขที่ {policy.doc_no} · ฉบับที่ {policy.version}</p>
              {policy.summary && <p className="text-sm text-gray-600 mt-2">{policy.summary}</p>}
            </div>
          </div>
        </section>

        <section className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="font-semibold">เอกสารฉบับเต็ม</p>
            {policy.ack_required && !policy.acked && (
              <p className="text-xs text-gray-500">อ่าน PDF ถึงหน้าสุดท้าย แล้วทำเครื่องหมายยืนยันใต้ตัวเอกสาร</p>
            )}
          </div>
          <ProtectedPolicyPdf
            endpoint={policy.file_url}
            title={policy.title}
            requireEndConfirmation={policy.ack_required && !policy.acked}
            reachedEnd={reachedEnd}
            onReachedEnd={() => setReachedEnd(true)}
          />
        </section>

        {policy.ack_required && policy.acked ? (
          <section className="card p-5 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <p className="font-semibold text-green-700 mt-2">รับทราบเอกสารฉบับนี้แล้ว</p>
            {policy.acked_at && <p className="text-sm text-gray-500 mt-1">บันทึกเมื่อ {new Date(policy.acked_at).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })}</p>}
          </section>
        ) : !policy.ack_required ? (
          <section className="card p-4 text-sm text-gray-600">
            เอกสารฉบับนี้เผยแพร่เพื่อให้ทราบ ไม่บังคับกดรับทราบ
          </section>
        ) : (
            <section className="card p-4 space-y-4">
              {!reachedEnd && (
                <p className="text-sm text-amber-700">ยังรับทราบไม่ได้ กรุณาเลื่อนอ่านเอกสารให้ถึงท้าย</p>
              )}
              {reachedEnd && needsSignature && <SignatureSvgPad onChange={setSignature} />}
              <label className="flex items-start gap-3 min-h-11 text-sm">
                <input
                  type="checkbox"
                  className="w-5 h-5 mt-0.5 shrink-0"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={!reachedEnd || acknowledge.isPending}
                />
                <span>ข้าพเจ้าได้เปิดอ่านและเข้าใจเอกสารฉบับนี้แล้ว และรับทราบว่าจะต้องปฏิบัติตาม</span>
              </label>
              <button className="btn-cta w-full min-h-12" onClick={() => void submitAck()} disabled={!canAck || acknowledge.isPending}>
                <ShieldCheck className="w-5 h-5" /> {acknowledge.isPending ? 'กำลังบันทึก…' : 'รับทราบเอกสาร'}
              </button>
              {!canAck && reachedEnd && (
                <p className="text-xs text-amber-700 text-center">
                  {needsSignature && !signature ? 'กรุณาวาดลายเซ็นและติ๊กยืนยัน' : 'กรุณาติ๊กยืนยันก่อนกดรับทราบ'}
                </p>
              )}
            </section>
        )}
      </div>
    </PageLayout>
  );
}
